import { useEffect, useMemo, useState } from "react";
import algoliasearch from "algoliasearch/lite";
import history from "instantsearch.js/es/lib/routers/history.js";
import { useSearchBox } from "react-instantsearch";

export const useAlgoliaClient = (appIdProp, searchKeyProp) => {
  const envAppId =
    import.meta.env.VITE_ALGOLIA_APPLICATION_ID ||
    import.meta.env.ALGOLIA_APPLICATION_ID ||
    "";
  const envSearchKey =
    import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY ||
    import.meta.env.ALGOLIA_SEARCH_API_KEY ||
    "";
  const appId = appIdProp ?? envAppId;
  const searchKey = searchKeyProp ?? envSearchKey;

  const client = useMemo(() => {
    if (!appId || !searchKey) return null;
    return algoliasearch(appId, searchKey);
  }, [appId, searchKey]);

  return { client, appId, searchKey };
};

export const decodeEntities = (value) => {
  if (!value || typeof value !== "string") return value;
  if (typeof document === "undefined") return value;
  const el = document.createElement("textarea");
  el.innerHTML = value;
  return el.value;
};

export const trimExcerpt = (text, max = 200) => {
  if (!text) return "";
  const cleaned = String(text).replace(/<[^>]*>/g, "");
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max).trimEnd()}…`;
};

export const formatHighlightedField = (hit, field = "headline") => {
  const raw =
    hit?._highlightResult?.[field]?.value ??
    hit?.[field] ??
    "";
  const decoded = decodeEntities(raw);
  return decoded.replace(/<em>/g, "<mark>").replace(/<\/em>/g, "</mark>");
};

export const buildPaginationPages = (current, total) => {
  if (total <= 5) {
    return Array.from({ length: total }, (_, idx) => idx + 1);
  }
  if (current <= 3) {
    return [1, 2, 3, 4, "ellipsis", total];
  }
  if (current >= total - 2) {
    return [1, "ellipsis", total - 3, total - 2, total - 1, total];
  }
  return [
    1,
    "ellipsis",
    current - 1,
    current,
    current + 1,
    "ellipsis",
    total,
  ];
};

export const createQueryPageRouting = ({
  indexName,
  mapStateToRoute,
  mapRouteToState,
} = {}) => ({
  router: history({ cleanUrlOnDispose: false }),
  stateMapping: {
    stateToRoute(uiState) {
      const state = uiState[indexName] || {};
      const base = {
        query: state.query || undefined,
        page: state.page && state.page > 1 ? state.page : undefined,
      };
      if (typeof mapStateToRoute === "function") {
        return mapStateToRoute(state, base) || base;
      }
      return base;
    },
    routeToState(routeState) {
      const base = {
        [indexName]: {
          query: routeState.query || "",
          page: routeState.page || 1,
        },
      };
      if (typeof mapRouteToState === "function") {
        const extraState = mapRouteToState(routeState) || {};
        return {
          [indexName]: {
            ...base[indexName],
            ...extraState,
          },
        };
      }
      return base;
    },
  },
});

export const useSearchInput = ({
  onChange,
  onSubmit,
  onReset,
} = {}) => {
  const { query, refine, clear } = useSearchBox();
  const [value, setValue] = useState(query || "");

  useEffect(() => {
    setValue(query || "");
  }, [query]);

  const handleChange = (event) => {
    const next = event?.currentTarget?.value ?? "";
    setValue(next);
    refine(next);
    onChange?.(next);
  };

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    onSubmit?.(value);
  };

  const handleReset = (event) => {
    event?.preventDefault?.();
    setValue("");
    clear();
    onChange?.("");
    onReset?.();
  };

  return {
    value,
    onChange: handleChange,
    onSubmit: handleSubmit,
    onReset: handleReset,
  };
};
